// DepositForm — on-chain R₿L deposit into the Vault (transfer to treasury,
// then server-side verification). Self-contained wagmi providers.
import React, { useState } from "react";
import { WagmiProvider, useAccount, useConnect, useDisconnect, useSwitchChain, useReadContract, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { parseUnits, formatUnits } from "viem";
import { Wallet, Loader2, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";
import { wagmiConfig, BASE_CHAIN_ID } from "../../web3/wagmiConfig";
import { RBL_CONTRACT, TREASURY_WALLET, rblTransferAbi } from "../../web3/rblPay";
import { API } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { tVault } from "../../i18n/vault";

const MIN_DEPOSIT = 100000;
const fmt = (n) => Number(n).toLocaleString();

function DepositInner({ onDeposited }) {
  const { lang } = useLanguage();
  const t = (k) => tVault(lang, k);
  const { user, token, openLogin } = useAuth();

  const { address, isConnected, chainId: walletChainId } = useAccount();
  const { connectors, connect, isPending: isConnecting } = useConnect();
  const { disconnect } = useDisconnect();
  const { switchChain, isPending: isSwitching } = useSwitchChain();
  const wrongNetwork = isConnected && walletChainId !== BASE_CHAIN_ID;

  const [amount, setAmount] = useState("");
  const [recordError, setRecordError] = useState(null);
  const [recorded, setRecorded] = useState(null);

  const { data: balance } = useReadContract({
    address: RBL_CONTRACT, abi: rblTransferAbi, functionName: "balanceOf",
    args: [address], chainId: BASE_CHAIN_ID,
    query: { enabled: isConnected && !!address, refetchInterval: 20_000 },
  });

  const { data: txHash, isPending: isWalletConfirm, writeContract, error: txError, reset: resetTx } = useWriteContract();
  const { isLoading: isMining, isSuccess: isMined } = useWaitForTransactionReceipt({ hash: txHash, chainId: BASE_CHAIN_ID });

  // After on-chain confirmation → record server-side
  React.useEffect(() => {
    if (!isMined || !txHash || recorded) return;
    (async () => {
      try {
        const r = await fetch(`${API}/vault/deposit`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ wallet_address: address, tx_hash: txHash }),
        });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d?.detail?.error || "deposit_record_failed");
        setRecorded(d);
        setAmount("");
        onDeposited?.();
      } catch (e) {
        setRecordError(e.message);
      }
    })();
  }, [isMined, txHash, recorded, address, token, onDeposited]);

  const amountNum = Number(amount) || 0;
  const belowMin = amountNum > 0 && amountNum < MIN_DEPOSIT;
  const insufficient = balance != null && amountNum > 0 && parseUnits(String(amountNum), 18) > balance;
  const busy = isWalletConfirm || isMining;

  const submit = () => {
    setRecordError(null);
    setRecorded(null);
    writeContract({
      address: RBL_CONTRACT, abi: rblTransferAbi, functionName: "transfer",
      chainId: BASE_CHAIN_ID,
      args: [TREASURY_WALLET, parseUnits(String(amountNum), 18)],
    });
  };

  if (!user) {
    return (
      <button onClick={openLogin} data-testid="vault-deposit-login"
        className="w-full rounded-xl border border-amber-400/40 bg-amber-400/10 py-3 text-xs font-black uppercase tracking-[0.18em] text-amber-200 hover:bg-amber-400/20">
        {t("loginFirst")}
      </button>
    );
  }

  if (recorded) {
    return (
      <div className="rounded-xl border border-emerald-400/40 bg-emerald-400/10 p-4 text-center" data-testid="vault-deposit-success">
        <CheckCircle2 className="mx-auto h-7 w-7 text-emerald-400" />
        <p className="mt-2 text-sm font-black text-white">{t("depositSuccess")}</p>
        <p className="mt-1 font-mono text-lg font-bold text-emerald-300">{fmt(recorded.amount_rbl)} R₿L</p>
        <p className="mt-1 text-[11px] text-white/55">
          {t("firstYield")}: {(recorded.first_yield_date || "").slice(0, 10)}
        </p>
        <a href={`https://basescan.org/tx/${txHash}`} target="_blank" rel="noopener noreferrer"
          className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold text-emerald-300 underline">
          <ExternalLink className="h-3 w-3" /> {t("viewTx")}
        </a>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div data-testid="vault-deposit-connect">
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">{t("selectWallet")}</p>
        <div className="space-y-2">
          {connectors.map((c) => (
            <button key={c.uid} disabled={isConnecting} onClick={() => connect({ connector: c })}
              className="flex w-full items-center justify-between rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-left text-sm font-bold text-white transition-all hover:border-amber-400/50 disabled:opacity-50">
              <span>{c.id === "injected" ? "MetaMask / Browser" : c.name}</span>
              <Wallet className="h-4 w-4 text-amber-300" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (wrongNetwork) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-xl border border-amber-400/50 bg-amber-400/[0.08] px-3 py-3">
        <span className="flex items-center gap-1.5 text-[11px] font-bold text-amber-300">
          <AlertTriangle className="h-3.5 w-3.5" /> {t("wrongNet")}
        </span>
        <button onClick={() => switchChain({ chainId: BASE_CHAIN_ID })} disabled={isSwitching}
          className="shrink-0 rounded-full bg-amber-400 px-3 py-1.5 text-[11px] font-black uppercase text-black disabled:opacity-50">
          {t("switchBase")}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="vault-deposit-form">
      <div className="flex items-center justify-between text-[11px] text-white/55">
        <span className="font-mono">{address?.slice(0, 6)}…{address?.slice(-4)}</span>
        <span>
          {t("balance")}: <b className="text-white">{balance != null ? fmt(Math.floor(Number(formatUnits(balance, 18)))) : "…"} R₿L</b>
        </span>
      </div>
      <input
        type="number" min="0" step="any" inputMode="decimal" placeholder={t("minDeposit")}
        value={amount} onChange={(e) => setAmount(e.target.value)}
        data-testid="vault-deposit-amount"
        className="mt-2 w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 font-mono text-lg font-bold text-white outline-none placeholder:text-white/25 focus:border-amber-400/60"
      />
      {belowMin && <p className="mt-1 text-[11px] text-red-400">{t("minDeposit")}</p>}
      <button
        onClick={submit}
        disabled={busy || amountNum < MIN_DEPOSIT || insufficient}
        data-testid="vault-deposit-btn"
        className="mt-3 w-full rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 py-3 text-xs font-black uppercase tracking-[0.2em] text-black transition-all hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-40"
      >
        {busy ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            {isWalletConfirm ? t("walletConfirm") : t("processing")}
          </span>
        ) : (
          t("deposit")
        )}
      </button>
      <button onClick={() => disconnect()} data-testid="vault-disconnect-btn"
        className="mt-2 w-full rounded-xl border border-white/15 py-2 text-[10px] font-black uppercase tracking-[0.18em] text-white/50 transition-colors hover:border-rose-400/40 hover:text-rose-300">
        {t("disconnect")} · {address?.slice(0, 6)}…{address?.slice(-4)}
      </button>
      {(txError || recordError) && (
        <div className="mt-2 rounded-lg border border-red-400/30 bg-red-400/[0.06] px-3 py-2" data-testid="vault-deposit-error">
          <p className="break-words text-[11px] text-red-300">
            {recordError || txError?.shortMessage || txError?.message}
          </p>
          <button onClick={() => { resetTx(); setRecordError(null); }} className="mt-1 text-[11px] font-black uppercase text-amber-300 underline">
            {t("retry")}
          </button>
        </div>
      )}
    </div>
  );
}

const queryClient = new QueryClient();

export default function DepositForm({ onDeposited }) {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <DepositInner onDeposited={onDeposited} />
      </QueryClientProvider>
    </WagmiProvider>
  );
}
